import { useEffect, useRef, useState } from 'react';

import { ScrollArea, ScrollBar } from '../../primitives/scroll-area';
import { cn } from '../../utils/cn';

interface TableResponsiveWrapperProps {
  data?: any[]; // Optional, can be used to trigger re-calculation
}
export function TableResponsiveWrapper({
  children,
  data,
}: React.PropsWithChildren<TableResponsiveWrapperProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoScroll = (direction: 'left' | 'right') => {
    const container = containerRef.current;
    if (!container) return;

    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;

    // Clear any existing interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    // Start scrolling
    scrollIntervalRef.current = setInterval(() => {
      const scrollAmount = direction === 'left' ? -10 : 10;
      viewport.scrollLeft += scrollAmount;
    }, 16); // ~60fps
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Find the viewport element inside ScrollArea
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = viewport;
      setShowLeftShadow(scrollLeft > 0);
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    // Check initial state
    const checkScrollable = () => {
      handleScroll();
    };

    // Use setTimeout to ensure content is rendered
    setTimeout(checkScrollable, 100);

    viewport.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkScrollable);

    return () => {
      viewport.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkScrollable);
      stopAutoScroll();
    };
  }, [data]);

  return (
    <div className="relative grid w-full" ref={containerRef}>
      {/* Left shadow */}
      <div
        className={cn(
          'pointer-events-none absolute top-0 left-0 z-10 h-full w-4 bg-linear-to-r from-foreground/5 to-transparent transition-opacity duration-300 dark:from-background/70',
          {
            'opacity-100': showLeftShadow,
            'opacity-0': !showLeftShadow,
          },
        )}
      />
      {/* Right shadow */}
      <div
        className={cn(
          'pointer-events-none absolute top-0 right-0 z-10 h-full w-4 bg-linear-to-l from-foreground/5 to-transparent transition-opacity duration-300 dark:from-background/70',
          {
            'opacity-100': showRightShadow,
            'opacity-0': !showRightShadow,
          },
        )}
      />
      {/* Left scroll zone */}
      <div
        className={`absolute top-0 left-0 z-20 h-full w-4 cursor-w-resize bg-linear-to-r to-transparent transition-opacity duration-300 hover:from-primary/10`}
        onMouseEnter={() => startAutoScroll('left')}
        onMouseLeave={stopAutoScroll}
      />
      {/* Right scroll zone */}
      <div
        className={`absolute top-0 right-0 z-20 h-full w-4 cursor-e-resize bg-linear-to-l to-transparent transition-opacity duration-300 hover:from-primary/10`}
        onMouseEnter={() => startAutoScroll('right')}
        onMouseLeave={stopAutoScroll}
      />
      <ScrollArea className="w-full [&_[data-orientation=vertical]]:hidden [&>[data-radix-scroll-area-viewport]]:overflow-y-clip!">
        {children}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
